// TPTalk FontSizePicker — 메시지 폰트 크기 5단계 토글.
// localStorage 'tpt-font-size' = sm|md|lg|xl|2xl. html[data-font-size=N] 박음. 기본값 'lg' (가독성 우선).
// 컴포넌트는 index.css 의 5단계 CSS 변수 + tailwind 토큰 text-tpt-xs/sm/base/lg/xl 로 연동.
import React, { useEffect, useState } from 'react';

export type FontSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const ALL: FontSize[] = ['sm', 'md', 'lg', 'xl', '2xl'];
const DEFAULT: FontSize = 'lg';

// 단계별 미리보기 '가' 폰트 크기 (px) — 단계간 1~2px 차이로 가독성 명확화.
const SIZES: Array<{ v: FontSize; label: string; px: number }> = [
  { v: 'sm',  label: '작게',     px: 12 },
  { v: 'md',  label: '조금작게', px: 13 },
  { v: 'lg',  label: '보통',     px: 14 },
  { v: 'xl',  label: '크게',     px: 15 },
  { v: '2xl', label: '아주크게', px: 17 },
];

const USER_KEY = 'tpt-font-size-user';   // 사용자가 직접 고른 값 표시 (2026-10-02)

export function readSavedSize(): FontSize {
  if (typeof localStorage === 'undefined') return DEFAULT;
  const v = localStorage.getItem('tpt-font-size');
  // 🔴 사용자가 고른 값만 존중 (2026-10-02 "고해상도 모니터에서 크기 이상") — 예전엔 부팅이 창 폭 기본값('xl')을
  //   저장해, 좁은 위젯·팝아웃 창 한 번으로 데스크톱 메시지 글자가 '크게'로 굳었다. 마커 없는 저장값은 기본값 취급.
  if (localStorage.getItem(USER_KEY) === '1' && ALL.includes(v as FontSize)) return v as FontSize;
  // 📱 모바일 기본 확대 (2026-07-09) — 폰 글자가 작다는 피드백. 사용자가 고른 값이 없을 때만 xl.
  try { if (typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches) return 'xl'; } catch { /* */ }
  return DEFAULT;
}

/** opts.user = 사용자가 직접 고른 값(저장). 부팅 기본값은 속성만 박고 저장하지 않는다 — 다른 창으로 번지지 않게. */
export function applySize(s: FontSize, opts?: { user?: boolean }) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-font-size', s);
  if (opts?.user) {
    try { localStorage.setItem('tpt-font-size', s); localStorage.setItem(USER_KEY, '1'); } catch { /* private */ }
  }
}

// 부트 함수 — 페이지 로드 시 즉시 호출 (main.tsx 에서).
export function bootFontSize() {
  applySize(readSavedSize());
}

export function FontSizePicker(): JSX.Element {
  const [size, setSize] = useState<FontSize>(readSavedSize());
  useEffect(() => { applySize(size, { user: true }); }, [size]);

  return (
    <div className="tpt-fontsize-picker" role="radiogroup" aria-label="폰트 크기"
      style={{
        display: 'inline-flex', gap: 4, padding: 4,
        background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 12,
      }}>
      {SIZES.map((s) => (
        <button key={s.v} type="button" role="radio" aria-checked={size === s.v}
          data-active={size === s.v}
          data-testid={`fontsize-${s.v}`}
          onClick={() => setSize(s.v)}
          style={{
            border: 'none',
            background: size === s.v ? '#FEE500' : 'transparent',
            color: size === s.v ? '#191919' : '#6B7280',
            cursor: 'pointer', padding: '6px 12px', borderRadius: 10,
            fontWeight: 600, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
          }}>
          <span style={{ fontSize: s.px, lineHeight: 1 }}>가</span>
          <span style={{ fontSize: 10, opacity: 0.8 }}>{s.label}</span>
        </button>
      ))}
    </div>
  );
}

export default FontSizePicker;
